import { RestClient } from "./restClient";

export type DocumentScope = "user" | "department" | "company" | "platform";
export type DocumentStatus = "processing" | "ready" | "failed" | string;

export interface DocumentRow {
  id: number;
  company_id: number;
  user_id: number | null;
  /** Owning department. `egpt_documents.dept_id` is a TEXT column (V7), so the
   *  API hands this back as a string like "7" — run it through
   *  {@link parseDocDeptId} before comparing it to a numeric department id. */
  dept_id: string | number | null;
  scope: DocumentScope;
  title: string;
  filename: string;
  source_url: string | null;
  content_type: string;
  mime_type: string;
  byte_size: number;
  status: DocumentStatus;
  error: string | null;
  chunk_count: number | null;
  created_at: string | null;
  last_synced_at: string | null;
  deleted_at: string | null;
  /** UUID of the project this document is scoped to, or null when it is a
   *  general (unscoped) knowledge/attachment document. */
  project_id: string | null;
  /** The page a kept web page came from; null for any other document. */
  origin_url?: string | null;
  /** Whether the caller may delete / re-index / re-scope / share this
   *  document — the server's per-document write rule (D5), sent by the list
   *  and the single-document GET. Drives which actions the UI offers; the
   *  server re-checks every write. Absent means read-only. */
  can_write?: boolean;
  /** What a `duplicate` / `duplicate_personal` row duplicates; null otherwise. */
  duplicate_of_document_id?: number | null;
}

export interface GroupedDocuments {
  platform: DocumentRow[];
  company: DocumentRow[];
  department: DocumentRow[];
  user: DocumentRow[];
}

/** The list endpoint's body: the tiers, plus why the Platform tier is empty
 *  when there is a reason the page should explain. Kept off GroupedDocuments
 *  so its keys stay exactly the tiers. */
export interface DocumentsListing extends GroupedDocuments {
  /** "no_industry": a platform document belongs to one industry, and the
   *  caller's company has none registered. "disabled": the company's policy
   *  turned platform knowledge off. Absent otherwise — and always for a
   *  platform admin, who sees the whole platform library. */
  platform_unavailable_reason?: "no_industry" | "disabled";
}

/** The largest file the upload route accepts. unified_agent's
 *  core_documents_routes.MAX_UPLOAD_BYTES is the authority and must match
 *  (test_upload_size_limit.py checks); refusing here first fails an oversized
 *  file at once, instead of after a long upload ends in the proxy's 413. */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** The same sentence the upload route returns with its 413. */
export const UPLOAD_TOO_LARGE_MESSAGE = `File exceeds the ${MAX_UPLOAD_BYTES / 1024 / 1024} MB upload limit.`;

export interface UploadInput {
  file: File;
  tier?: DocumentScope;
  dept_id?: number | null;
  /** "knowledge" → chunk + embed + index so the doc is searchable (the
   *  backend default). "attachment" → extract text only (chat attachments). */
  intent?: "knowledge" | "attachment";
  /** Scope the uploaded document to a project (UUID). Project knowledge is
   *  only retrieved when chatting inside that project. */
  project_id?: string | null;
}

export interface VisibilityUpdateInput {
  scope: "department" | "company";
  /** Required when `scope === "department"`; omitted for `"company"`. */
  dept_ids?: number[];
  /** Moving a PERSONAL document into several departments: which of
   *  `dept_ids` owns it (the others get read-only access). Omitted with a
   *  single entry, that entry owns it. Rejected for any other document. */
  owner_dept_id?: number;
}

export interface VisibilityUpdateResult {
  document_id: number;
  scope: DocumentScope;
  /** Owning department after the call (a personal document promoted to a
   *  department moves into it); null for a company-wide document. */
  dept_id: string | null;
  /** Further departments with read-only grants — never the owning one. */
  granted_dept_ids: string[];
}

/** Keep some of the web pages an answer used, each as a personal document
 *  (spec §3.3). The server only accepts URLs that are the stored answer's
 *  own web citations, and fetches each page in the background. */
export interface KeepWebPagesInput {
  session_id: string;
  message_id: string;
  urls: string[];
}

export interface KeptWebPage {
  /** The stored citation URL the page was kept from. */
  url: string;
  document_id: number;
  /** "processing": being fetched; "duplicate": already kept, nothing fetched. */
  status: "processing" | "duplicate";
}

export interface KeepWebPagesResult {
  documents: KeptWebPage[];
}

/** Coerce a document's `dept_id` to the numeric id the /departments API uses.
 *  Returns null when absent or unparseable so callers drop the row rather than
 *  compare against NaN. */
export function parseDocDeptId(
  raw: string | number | null | undefined,
): number | null {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
}

export class DocumentsApi {
  constructor(private client: RestClient) {}

  list(status?: DocumentStatus): Promise<DocumentsListing> {
    const path = status ? `/core/v1/documents?status=${encodeURIComponent(status)}` : "/core/v1/documents";
    return this.client.get<DocumentsListing>(path);
  }

  /** Lists the documents scoped to a single project. The backend returns the
   *  tier-grouped shape; project files are typically all in the `user` tier
   *  but we flatten across tiers so the caller gets one list. */
  async listByProject(projectId: string): Promise<DocumentRow[]> {
    const grouped = await this.client.get<GroupedDocuments>(
      `/core/v1/documents?project_id=${encodeURIComponent(projectId)}`,
    );
    return [
      ...grouped.platform,
      ...grouped.company,
      ...grouped.department,
      ...grouped.user,
    ];
  }

  get(id: number): Promise<DocumentRow> {
    return this.client.get<DocumentRow>(`/core/v1/documents/${id}`);
  }

  async upload(input: UploadInput): Promise<{ document_id: number; status: string }> {
    if (input.file.size > MAX_UPLOAD_BYTES) throw new Error(UPLOAD_TOO_LARGE_MESSAGE);
    const fd = new FormData();
    fd.append("file", input.file);
    fd.append("tier", input.tier ?? "user");
    if (input.dept_id != null) fd.append("dept_id", String(input.dept_id));
    if (input.intent) fd.append("intent", input.intent);
    if (input.project_id) fd.append("project_id", input.project_id);
    // NOTE: do NOT set Content-Type — the browser sets the multipart
    // boundary header itself. RestClient.raw() honors the init headers
    // we pass, but here we deliberately pass none.
    const resp = await this.client.raw("/core/v1/documents/upload", {
      method: "POST",
      body: fd,
    });
    return (await resp.json()) as { document_id: number; status: string };
  }

  async remove(id: number): Promise<void> {
    await this.client.raw(`/core/v1/documents/${id}`, { method: "DELETE" });
  }

  /** Adds an already-attached file to the searchable knowledge base.
   *  Backend responds with the document id and its (re)indexing status. */
  async promote(documentId: number): Promise<{ document_id: number; status: string }> {
    return this.client.post(`/core/v1/documents/${documentId}/promote`, {});
  }

  /** Keeps the picked web pages of an answer as personal documents. */
  keepWebPages(input: KeepWebPagesInput): Promise<KeepWebPagesResult> {
    return this.client.post("/core/v1/documents/from-web-pages", input);
  }

  /** `{url: document_id}` for the answer's web pages the caller already
   *  keeps, keyed by the stored citation URL. */
  async keptWebPages(sessionId: string, messageId: string): Promise<Record<string, number>> {
    const query = new URLSearchParams({ session_id: sessionId, message_id: messageId });
    const res = await this.client.get<{ kept: Record<string, number> }>(
      `/core/v1/documents/from-web-pages/kept?${query.toString()}`,
    );
    return res.kept ?? {};
  }

  /** Widens a document's visibility beyond its current tier: a personal
   *  document moves into its owning department (`owner_dept_id`, or the only
   *  `dept_ids` entry) and the other `dept_ids` get read-only access; a
   *  department document is shared read-only with more departments; either
   *  can go to the whole company. Mutually exclusive per the
   *  backend's `VisibilityUpdate` — a single call carries either
   *  `scope: "department"` + `dept_ids`, or `scope: "company"` alone. */
  setVisibility(
    documentId: number,
    input: VisibilityUpdateInput,
  ): Promise<VisibilityUpdateResult> {
    return this.client.post(`/core/v1/documents/${documentId}/visibility`, input);
  }

  /** Returns the URL a browser can hit to download the file. The
   *  backend responds 302 to a presigned S3 URL. Caller may inject as
   *  href on an <a> tag. */
  downloadHref(id: number): string {
    // Note: this can't include auth as a header on a plain link, so
    // anonymous proxies that re-route /documents may need a separate
    // signed-url endpoint. The /documents/{id}/download path is the
    // backend-supported route.
    return `/core/v1/documents/${id}/download`;
  }
}
